<template>
  <div class="termination-box">
    <p class="alert-contract-terminated text-center">
      <strong>{{ terminatedTitle }} {{ schedule.terminated_date.substring(0, 10) }}</strong><br>
      <strong>{{ terminationSentTitle }} {{ schedule.terminated_send_date }}</strong><br>
      <strong v-if="schedule.case_is_explained_date !== null">
        Umowa w egzekucji. Data rozpoczęcia egzekucji {{ schedule.case_is_explained_date }}<br>
      </strong>
      <d-button
        :class="{
          'm-top ml-2 mr-2 mb-1': true,
          'isLoading': exportingTerminationInProgress
        }"
        theme="success"
        @click.prevent="exportTerminationToPdf(schedule.id)">
        <i class="material-icons">save_alt</i> Pobierz podsumowanie
      </d-button>
      <d-button
        :class="{
          'm-top ml-2 mr-2 mb-1': true,
          'isLoading': exportingTerminationLoanReportInProgress
        }"
        theme="success"
        @click.prevent="exportTerminationLoanReport(schedule.id)">
        <i class="material-icons">save_alt</i> Podsumowanie do sądu
      </d-button>
        <d-button
            v-if="isAdmin"
            :class="{
          'm-top ml-2 mr-2 mb-1': true,
          'isLoading': exportingTerminationLoanReportInProgress
        }"
            theme="danger"
            @click.prevent="cancelTermination(schedule.id)">
            {{ cancelButtonText }}
        </d-button>
    </p>

    <table
      class="table mb-0 responsive-table text-center"
      style="border: 1px #dee2e6 solid;table-layout: fixed;">
      <thead class="bg-light">
        <tr>
          <th>{{ dateHeaderText }}</th>
          <th>{{ interestHeaderText }}</th>
          <th>Pozostało do spłaty</th>
          <th>Sygnatura</th>
          <th>Akcje</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <dt>{{ dateHeaderText }}</dt>
            <dd>{{ schedule.terminated_date.substring(0, 10) }}</dd>
          </td>
          <td>
            <dt>{{ interestHeaderText }}</dt>
            <dd
              v-if="!schedule.terminatedPaymentDate"
              class="text-success">
              <strong>{{ toNumber(schedule.terminated_interest) }} zł</strong>
            </dd>
            <dd v-else>{{ toNumber(schedule.terminated_interest) }} zł</dd>
          </td>
          <td>
            <dt>Pozostało do spłaty</dt>
            <dd>
              {{ toNumber(schedule.to_finish_credit) }}
            </dd>
          </td>
          <td>
            <dt>Sygnatura</dt>
            <dd v-if="!schedule.clause_signature || schedule.clause_signature.length < 1" >
              <d-button @click.prevent="isSignatureModalVisible = true">
                Wysłano wniosek o nadanie klauzuli wykonalności
              </d-button>
            </dd>
            <dd v-else-if="schedule.clause_signature.status === 1 && !schedule.clause_signature.signature_number">
              <d-button
                @click.prevent="isSignatureGivenModalVisible = true"
                theme="success"
                class="mb-2">
                Nadano sygnaturę akt klauzuli wykonalności
              </d-button>
              <d-button
                @click.prevent="declinedSignature"
                theme="danger">
                Nie nadano sygnatury
              </d-button>
            </dd>
            <dd v-else-if="schedule.clause_signature.signature_number">
              Numer sygnatury: <span class="text-success">{{ schedule.clause_signature.signature_number }}</span>
            </dd>
            <dd
              v-else
              class="text-danger">
              Nie nadano sygnatury
            </dd>
          </td>
          <td>
            <dt>Akcje</dt>
            <dd
              v-if="schedule.to_finish_credit > 0"
              class="date-cell-wrapper"
              style="min-height: 35px;">
              <div>
                <d-button>Dodaj&nbsp;wpłatę</d-button>
              </div>
              <ss-date
                @picked="contractPaid"
                type="text"
                name="terminated-payment-date"
                v-model="terminatedPaymentDate"
                placeholder=""/>
            </dd>
            <dd v-else class="text-success">
              <strong>Opłacono całość</strong>
            </dd>
          </td>
        </tr>
      </tbody>
    </table>

    <signature-modal
      :is-modal-visible="isSignatureModalVisible"
      :schedule-id="schedule.id"/>

    <signature-given-modal
      :clause-signature-id="schedule.clause_signature.id"
      :is-modal-visible="isSignatureGivenModalVisible"/>

    <paid-installment-modal
      :amount-prop="amount"
      :is-modal-visible="isPaidInstallmentModalVisible"
      :scheduleId="schedule.id"
      :terminated-payment-date="terminatedPaymentDate"/>
  </div>
</template>

<script>
import FileSaver from 'file-saver';
import SsDate from '@/components/common/SsDate';
import SignatureModal from '@/components/client/contract-partials/basic-info/SignatureModal.vue';
import SignatureGivenModal from '@/components/client/contract-partials/basic-info/SignatureGivenModal.vue';
import PaidInstallmentModal from '@/components/client/contract-partials/basic-info/PaidInstallmentModal.vue';
import ssPriceInput from "@/components/common/SsPriceInput";
import LarErrors from '@/helpers/Errors';

export default {
  name: 'ContractBasicInfoTerminationBox',
  components: {
    FileSaver,
    SsDate,
    SignatureModal,
    SignatureGivenModal,
    PaidInstallmentModal,
    ssPriceInput,
    LarErrors
  },
  props: {
    clientName: {
      default: '',
      type: String
    },
    schedule: {
      required: true,
      type: Object
    },
    isAdmin: {
        default: false,
    }
  },
  computed: {
    isVindication () {
      return this.schedule.contract_end_date_has_passed;
    },
    terminatedTitle () {
      return this.isVindication ? 'Rozpoczęto windykację dnia' : 'Umowa została wypowiedziana dnia';
    },
    terminationSentTitle () {
      return this.isVindication ? 'Wezwanie do spłaty wysłano dnia' : 'Wypowiedzenie wysłano dnia';
    },
    cancelButtonText () {
      return this.isVindication ? 'Cofnij windykację' : 'Cofnij wypowiedzenie';
    },
    dateHeaderText () {
      return this.isVindication ? 'Data rozpoczęcia windykacji' : 'Data wypowiedzenia';
    },
    interestHeaderText () {
      return this.isVindication ? 'Odsetki z windykacji' : 'Odsetki z wypowiedzenia';
    },
    cancelConfirmText () {
      return this.isVindication 
        ? 'Czy potwierdzasz, że chcesz wycofać windykację umowy?'
        : 'Czy potwierdzasz, że chcesz wycofać wypowiedzenie umowy?';
    },
    cancelSuccessText () {
      return this.isVindication ? 'Windykacja umowy została wycofana' : 'Wypowiedzenie umowy zostało wycofane';
    },
    filenameSuffix () {
      return this.isVindication ? 'podsumowanie_windykacji' : 'podsumowanie_wypowiedzenia';
    },
  },
  data () {
    return {
      amount: 0,
      exportingTerminationInProgress: false,
      exportingTerminationLoanReportInProgress: false,
      isPaidInstallmentModalVisible: false,
      isSignatureGivenModalVisible: false,
      isSignatureModalVisible: false,
      terminatedPaymentDate: '',
      lar_errors: LarErrors,
    }
  },
  mounted () {
    this.$bus.$on('signature-modal-close', this.closeSignatureModal);
    this.$bus.$on('paid-installment-close', this.closePaidInstallmentModal);
    this.$bus.$on('signature-given-modal-close', this.closeSignatureGiventModal);
  },
  methods: {
    exportTerminationToPdf (id) {
      this.exportingTerminationInProgress = true;

      axios.post('api/schedules/export-terminated-loan-to-pdf',
        { id: id },
        {responseType: 'blob'}
      ).then(response => {
        let blob = new File(
          [response.data],
          this.clientName + '_' + this.filenameSuffix + '_' +  Vue.getCurrentDate() + '.pdf',
          { type: 'application/pdf' }
        );
        FileSaver.saveAs(blob);
        this.exportingTerminationInProgress = false;
      }).catch( error => {
        this.exportingTerminationInProgress = false;
      })
    },
    exportTerminationLoanReport (id) {
      this.exportingTerminationLoanReportInProgress = true;

      axios.post('api/reports/download-terminated-loan-report',
        { schedule_id: id },
        {responseType: 'blob'}
      ).then(response => {
        let blob = new File(
          [response.data],
          this.clientName + '_podsumowanie_do_sadu_' +  Vue.getCurrentDate() + '.xlsx',
          { type: 'application/xlsx' }
        );
        FileSaver.saveAs(blob);
        this.exportingTerminationLoanReportInProgress = false;
      }).catch( error => {
        this.exportingTerminationLoanReportInProgress = false;
      })
    },
    cancelTermination(schedule_id){
      Vue.swal({
          title: 'Uwaga',
          html: this.cancelConfirmText,
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Tak',
          cancelButtonText: 'Nie',
          buttonsStyling: true
      }).then((submit) => {
          if (submit.value) {
              axios.post('api/schedules/cancel-terminate-loan-contract', {
                  id: schedule_id,
              }).then(response => {
                  Vue.swal({
                      type: 'success',
                      title: this.cancelSuccessText,
                      showConfirmButton: true,
                  }).then(() => {
                      this.$bus.$emit('reload-contract');
                  });
              }).catch((error) =>{
                  LarErrors.record(error.response.data.errors);
                  Vue.swal({
                      type: 'warning',
                      title: 'Wystąpił błąd',
                      text: error.response.data.error ? error.response.data.error : 'Spróbuj ponownie później',
                      showConfirmButton: true,
                  });
              })
          }
      });
    },
    declinedSignature(){
      Vue.swal({
        title: 'Uwaga',
        html: 'Czy potwierdzasz, że nie nadano sygnatury?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Tak',
        cancelButtonText: 'Nie',
        buttonsStyling: true
      }).then((submit) => {
        if (submit.value) {
          axios.post('api/clause-signatures/update-signature', {
            id: this.schedule.clause_signature.id,
            status: 0
          }).then(response => {
            Vue.swal({
              type: 'success',
              title: 'Potwierdzono brak nadania sygnatury.',
              showConfirmButton: true,
            }).then(() => {
              this.$bus.$emit('reload-contract');
            });
          }).catch((error) =>{
            LarErrors.record(error.response.data.errors);
            Vue.swal({
              type: 'warning',
              title: 'Wystąpił błąd',
              text: error.response.data.error ? error.response.data.error : 'Spróbuj ponownie później',
              showConfirmButton: true,
            });
          })
        }
      });
    },
    contractPaid (sum) {
      if (sum) {
        Vue.set(this, 'amount', sum);
      }
      this.isPaidInstallmentModalVisible = true;
    },
    toNumber (number) {
      return Vue.toNumber(number);
    },
    closeSignatureModal () {
      this.isSignatureModalVisible = false;
    },
    closePaidInstallmentModal () {
      this.isPaidInstallmentModalVisible = false;
    },
    closeSignatureGiventModal () {
      this.isSignatureGivenModalVisible = false;
    }
  },
  beforeDestroy () {
    this.$bus.$off('signature-modal-close', this.closeSignatureModal);
    this.$bus.$off('paid-installment-close', this.closePaidInstallmentModal);
    this.$bus.$off('signature-given-modal-close', this.closeSignatureGiventModal);
  }
}
</script>
